import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageSquareHeart, Reply } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOrgFeedSettings, useUpdateOrgFeedSettings } from "@/hooks/useOrgFeedSettings";
import { OrgAccessSettingsCard } from "@/components/corporate/organizations/access/OrgAccessSettingsCard";
import { EntraSyncStatusCard } from "@/components/corporate/organizations/EntraSyncStatusCard";
import { useOrgEntraSyncStatus } from "@/hooks/useOrgEntraSyncStatus";
import {
  useOrgReplyDisplayName,
  useUpdateOrgReplyDisplayName,
  REPLY_DISPLAY_NAME_MAX_LENGTH,
} from "@/hooks/useOrgReplyDisplayName";

interface SettingsAndFeaturesTabProps {
  /** Organization GUID — feed settings are scoped per organization. */
  organizationId: string;
}

export default function SettingsAndFeaturesTab({ organizationId }: SettingsAndFeaturesTabProps) {
  // Social Feed master switch — real, per-organization, persisted to the CMS API.
  const {
    data: feedSettings,
    isLoading: isFeedSettingsLoading,
  } = useOrgFeedSettings(organizationId);
  const updateFeedSettings = useUpdateOrgFeedSettings(organizationId);

  const handleSocialFeedToggle = (enabled: boolean) => {
    updateFeedSettings.mutate({
      masterFeedEnabled: enabled,
      feedEnabled: feedSettings?.feedEnabled ?? null,
      allowEmployeePosts: feedSettings?.allowEmployeePosts ?? null,
      allowComments: feedSettings?.allowComments ?? null,
      allowReactions: feedSettings?.allowReactions ?? null,
      allowImagePosts: feedSettings?.allowImagePosts ?? null,
      allowVideoPosts: feedSettings?.allowVideoPosts ?? null,
    });
  };

  // Microsoft Entra sync status — `isEnabled` is the source of truth for "signs in with Entra"
  // (locks the allowed-domain editor up front) and drives the read-only Microsoft sync card.
  const {
    data: entraSyncStatus,
    isLoading: isEntraSyncLoading,
    isError: isEntraSyncError,
  } = useOrgEntraSyncStatus(organizationId);
  // Until the status resolves the org is neither "Entra" nor "not Entra" — keep domains locked.
  const entraCheck = isEntraSyncError ? "failed" : isEntraSyncLoading ? "loading" : undefined;

  // Reply display name — real, per-organization, persisted to the CMS API.
  // The internal team manages any org's value; blank clears to the registered name.
  const {
    data: replyName,
    isLoading: isReplyNameLoading,
  } = useOrgReplyDisplayName(organizationId);
  const updateReplyName = useUpdateOrgReplyDisplayName(organizationId);

  const [replyNameInput, setReplyNameInput] = useState("");
  // Sync the local editor with the server value once it loads / changes.
  useEffect(() => {
    setReplyNameInput(replyName?.replyDisplayName ?? "");
  }, [replyName?.replyDisplayName]);

  const trimmedReplyName = replyNameInput.trim();
  const replyNameTooLong =
    trimmedReplyName.length > REPLY_DISPLAY_NAME_MAX_LENGTH;
  const replyNameDirty =
    trimmedReplyName !== (replyName?.replyDisplayName ?? "");

  const handleSaveReplyName = () => {
    if (replyNameTooLong) return;
    updateReplyName.mutate({ replyDisplayName: replyNameInput });
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Settings & Features</h2>
        <p className="text-sm text-muted-foreground">
          Manage features, sign-in and client-specific settings
        </p>
      </div>

      <div className="space-y-6">
        {/* Features Panel */}
        <Card>
          <CardHeader>
            <CardTitle>Features</CardTitle>
            <CardDescription>
              Switch features on or off for this organization
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Social Feed master switch — real, persisted per-organization */}
            <div
              className="flex items-center justify-between p-4 rounded-lg border"
              data-testid="social-feed-feature-row"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg text-primary">
                  <MessageSquareHeart className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-medium">Social Feed</h4>
                  <p className="text-sm text-muted-foreground">
                    Master switch for the employee social feed, stickers and kudos.
                    When off, the feed is hidden for all employees in this organization.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isFeedSettingsLoading ? (
                  <Skeleton className="h-6 w-11 rounded-full" />
                ) : (
                  <Switch
                    checked={!!feedSettings?.masterFeedEnabled}
                    onCheckedChange={handleSocialFeedToggle}
                    disabled={updateFeedSettings.isPending}
                    aria-label="Toggle Social Feed"
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sign-in & access — real, persisted per-organization. Its "Perks enabled" switch
            replaces the former mock "Global Marketplace Access" row (one perks toggle only). */}
        <OrgAccessSettingsCard
          organizationId={organizationId}
          hasEntraIntegration={!!entraSyncStatus?.isEnabled}
          entraCheck={entraCheck}
        />

        {/* Microsoft sync — read-only, shown only when Entra sync is enabled */}
        <EntraSyncStatusCard status={entraSyncStatus} />

        {/* Reply display name — real, persisted per-organization */}
        <Card data-testid="reply-display-name-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Reply className="h-4 w-4" />
              Reply display name
            </CardTitle>
            <CardDescription>
              The name this organization&apos;s replies to employee feedback
              are signed with.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {isReplyNameLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="reply-display-name">Display name</Label>
                  <Input
                    id="reply-display-name"
                    value={replyNameInput}
                    placeholder="e.g. People Team"
                    onChange={(e) => setReplyNameInput(e.target.value)}
                    aria-label="Reply display name"
                    aria-invalid={replyNameTooLong}
                    disabled={updateReplyName.isPending}
                  />
                  <p
                    className={cn(
                      "text-sm",
                      replyNameTooLong
                        ? "text-destructive"
                        : "text-muted-foreground",
                    )}
                  >
                    {replyNameTooLong
                      ? `Keep it to ${REPLY_DISPLAY_NAME_MAX_LENGTH} characters or fewer.`
                      : "Shown on this organization's replies to employee feedback — leave empty to use its registered company name."}
                  </p>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveReplyName}
                    disabled={
                      updateReplyName.isPending ||
                      replyNameTooLong ||
                      !replyNameDirty
                    }
                  >
                    {updateReplyName.isPending ? "Saving..." : "Save"}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
